import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Bus, CalendarClock, Radio, Route, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import CaseStudyCta from "../components/layout/CaseStudyCta";
import "../styles/vaulen-school.css";

// Every figure on this page comes from one route model: 750 modelled pupils in 1,329 homes,
// each routed on the OpenStreetMap footpath network to Vaulen School and to Sørmarka at 4 km/h.

// The average walk (9 → 31 min) is the hero's key figure, so it is not repeated here.
const highlights = [
  { label: "Pupils modelled", value: "750" },
  { label: "Homes in the catchment", value: "1,329" },
  { label: "Over 2 km from Sørmarka", value: "442 pupils" },
];

const timeline = [
  { when: "2027", what: "Planned move to the temporary campus at Sørmarka" },
  { when: "2029–30", what: "Earliest start of demolition and building at the current site" },
  { when: "2030s", what: "New school expected to open" },
];

// Modelled pupils per 5-minute band of walking time. The two schools' bands do not overlap:
// every walk to Vaulen today is under 15 minutes, every walk to Sørmarka is over 15.
const WALK_BANDS = [
  { from: 0, pupils: 93.8, site: "today" },
  { from: 5, pupils: 362.7, site: "today" },
  { from: 10, pupils: 293.3, site: "today" },
  { from: 15, pupils: 25.0, site: "interim" },
  { from: 20, pupils: 140.4, site: "interim" },
  { from: 25, pupils: 142.3, site: "interim" },
  { from: 30, pupils: 195.6, site: "interim" },
  { from: 35, pupils: 208.7, site: "interim" },
  { from: 40, pupils: 37.8, site: "interim" },
];
const BAND_MAX = 400;

// Map overlay, in the pixel space of /vaulen-basemap.jpg (891 × 686).
const MAP_W = 891;
const MAP_H = 686;
const at = (x, y) => ({ left: `${(x / MAP_W) * 100}%`, top: `${(y / MAP_H) * 100}%` });
const SCHOOLS = { vaulen: [432.2, 415.7], sormarka: [309.5, 226] };
const CROSSING = [360.3, 174.7];

// Modelled pupils per street segment, drawn in six width classes:
// 5–15, 15–40, 40–100, 100–200, 200–400 and 400+ pupils. Segments under 5 pupils are left out.
const FLOW_WIDTHS = [2.2, 3.2, 4.6, 6.4, 8.6, 11.5];
const FLOWS = {
  sormarka: [
    "M446 524L447 528 453 537 440 549M319 352L343 366M444 427L456 427 464 424 468 427 473 427M401 323L390 321M401 323L419 328M401 323L400 326M482 390L502 398 498 409 501 413 492 435 493 435 495 434 500 436M555 448L574 466 579 461M359 462L346 478 358 485 357 488M487 271L481 267M487 271L480 289M487 271L508 280 509 281 508 287 518 292 516 297M449 380L448 383 450 386 451 393 461 396M513 346L513 344 505 340 506 339 503 337M513 346L504 365 501 364 501 366 494 362 493 363 491 366M513 346L514 347 509 358 517 361M406 498L393 522 394 524 392 529 396 533M477 474L504 491 509 485M488 410L495 412 493 419 503 422M392 372L402 366 408 375 414 371M372 321L372 332 378 333M452 374L463 378M310 336L316 339 331 341 347 351M386 485L391 490 384 493 380 497M434 387L422 391 426 402 428 402M434 387L439 387M434 387L434 390M432 485L443 486 445 482 454 485 456 487 447 503M374 350L369 353 368 366 378 375 382 382M494 452L488 464 497 468M424 443L418 442 413 451 409 460M460 314L470 317M436 514L437 517 416 539M248 314L251 323 256 321 256 329 258 336 265 339 268 343 272 342 295 346 303 352 309 362 316 371 325 374 328 373 331 374 342 380 358 385 366 396 377 407M392 193L390 196 390 200 392 204 394 205 396 209 394 216 390 219 385 227 387 227 397 246 397 253 394 258 399 270 408 270 408 278 423 284 422 287 435 293 429 308 427 316 430 317 430 319 436 320 437 322 435 328M406 544L407 546 417 536 411 529M456 325L466 327 470 333 476 337 473 344 475 345 475 348 477 349 476 352 492 357 490 360 488 361M382 312L369 310 369 308 365 307 365 300 363 299 362 277 370 280 371 300M527 459L533 450 527 448M450 348L462 354M383 362L388 357 392 360M377 346L374 350M476 469L466 482 472 487M356 322L347 323 351 334M334 327L337 319 330 316 324 316M343 533L338 538 312 522 308 526 304 519 306 510 316 500 328 498 352 517M328 311L327 311M408 403L405 405 400 412M441 455L440 458 456 462M476 455L480 448 475 446M478 400L478 398M520 404L527 389 530 388 568 404 567 405 571 409 583 415 574 426M417 474L420 475",
    "M483 380L480 390M480 390L476 409 488 410M480 390L482 390M212 296L217 295 226 297 236 303 248 314 269 315 276 319 279 327 291 329 299 328 300 330 310 336 307 342 312 344 310 347 319 352M346 305L361 309 361 319 363 319 362 321 390 321M450 348L447 359 447 365 452 374 449 380M466 293L467 299 488 309 485 322 486 324 499 328 500 330 499 332 500 333 500 336 503 337M430 470L425 471 418 476 406 498M369 170L370 172 370 175 351 206 367 216 356 226 355 230 350 237 355 243 358 244 360 262 357 264 361 270 362 277M307 509L303 507 288 494 284 492 286 489 287 476 293 468 285 464 282 456 280 454 290 444 291 438 290 430 292 418 287 406 273 390 264 385 256 374 245 354 234 345 230 331 224 327 225 320 222 318 220 312 217 308 216 304 211 296M407 401L422 391M336 296L326 316M533 450L540 439 555 448M477 474L474 473 477 469 476 469 486 448 494 452M417 536L435 517 434 507 436 514 439 516 436 519 439 523 443 522 446 524M414 416L424 417 424 421 428 424 431 421 440 422 440 425 444 427M359 462L382 434 390 436 368 471 386 485M428 456L440 458M481 267L474 264 470 260 452 252 452 254",
    "M430 470L432 481 432 495 434 507M212 296L224 308 225 310 227 311 231 317 240 319 246 324 251 325 250 330 253 337 260 348 260 350 268 355 281 367 281 373 283 376 290 383 298 386 301 389 306 404 311 409 310 411 315 413 338 410 351 411 381 435M425 444L431 444 476 455 477 457 475 459 478 464M449 339L452 343 450 348M483 380L485 372 485 370 497 374 500 374 501 376 502 376 526 387 526 388 528 389 532 395 526 412 527 414 520 429 540 439",
    "M414 416L425 444 430 470M466 293L465 302 454 331 449 339 465 348 481 364 485 370M303 215L296 218 291 215 282 219 263 238 216 273 205 286 211 296 212 296",
    "M414 416L402 389 396 377 377 354 360 331 358 328 359 325 356 322 346 306 346 305 337 298 334 291 330 289 329 286 331 285 331 283 327 265 332 241 361 186 367 177 369 170 366 169 372 170 376 168 380 172 384 169 389 169 394 173 394 176 394 179 397 182 394 184 395 186 392 190 392 193 406 199 414 199 420 201 426 215 422 223 432 224 437 230 444 234 444 236 452 254 449 260 453 261 450 267 461 274 460 277 466 293",
    "M303 218L302 217 308 209 313 206 318 206 319 203 337 190 339 184 358 180 363 171 366 169",
  ],
  vaulen: [
    "M365 454L342 483M446 524L447 528 453 537 440 549M448 383L443 386 434 387 434 390M368 471L349 498M445 330L439 327 435 328 437 322 436 320 430 319 430 317 427 316M335 318L326 316M477 474L504 491 508 485M464 424L468 427 488 428 480 448 475 446M326 316L324 316M326 316L328 311 327 311M505 365L502 371 499 374 486 370 485 372M505 365L506 364M505 365L501 364 501 366 494 362 493 363 491 366M393 522L394 524 392 529M392 529L384 550M392 529L396 533M384 493L379 497M407 401L422 391 419 385M432 485L443 486 445 482 454 485 456 487 447 503M392 372L398 368 394 362 398 359M460 314L470 317M408 403L405 405 394 418M379 497L379 497M379 497L376 500M475 408L494 411M377 354L397 337M436 514L437 517 416 539M463 378L452 374 455 370 460 368 473 367M418 442L413 451 409 460M527 459L533 450 527 448M504 323L500 333 500 336 503 337 502 339 500 338 499 343 498 342 490 360 488 361M480 390L496 396M406 544L407 546 417 536 411 529M450 405L433 403M347 323L352 336M523 422L517 437M476 469L466 482 472 487M365 300L365 307 369 308 369 310 382 312M493 419L496 411 497 410 516 367 518 362 517 361M342 348L352 354 350 367 347 368 334 360M395 395L391 398M511 425L513 422M497 435L494 434 493 435 492 435 486 448 494 452 488 464 497 468M473 302L467 299 471 288 478 290 480 289M451 393L456 394M441 455L440 458 456 462M372 331L373 333 384 334 386 328M382 382L377 374M566 437L556 450 574 466 579 461M417 474L420 475",
    "M395 395L386 381 382 382 374 388 365 391 363 390 358 385 347 382 350 371 349 368M425 444L431 444 476 455 477 457 475 459 478 464 474 473 477 474M480 390L485 372 485 368 486 368 488 361M374 350L384 339 382 336 382 334M533 450L540 439 556 450M492 435L496 424 498 425 500 421M368 471L390 436 382 434 365 454M417 536L435 517 434 507 436 514 439 516 436 519 439 523 443 522 446 524M428 456L440 458M384 493L391 490 406 498 393 522M369 310L365 310 363 312 362 328 364 335 354 322 347 323 335 318M445 330L453 333 467 299",
    "M451 393L450 386 448 383 452 374 447 365 447 359 449 348 452 343 448 340 453 333M392 372L377 354 364 335M500 421L520 429 540 439M424 443L418 442 390 436M471 417L475 408 480 390M434 507L432 495 432 481 430 470 425 471 418 476 406 498M403 390L395 395",
    "M424 443L430 470M431 421L428 424 424 421 424 417 414 416 402 389 392 372M500 421L493 419 474 419 471 417 464 424 460 426 456 427 442 427 440 425 440 422 441 420 444 420 444 415 448 416 450 405 451 393",
    "M440 422L431 421 427 431 421 435 424 443",
    "",
  ],
};

// Legend samples: the 5–15, 100–200 and 400+ width classes.
const LEGEND = [
  { label: "5", width: FLOW_WIDTHS[0] },
  { label: "100", width: FLOW_WIDTHS[3] },
  { label: "400+", width: FLOW_WIDTHS[5] },
];

// The hero's average-walk figure doubles as the switch between the two sets of routes.
const WALKS = [
  {
    key: "vaulen",
    site: "Today · Vaulen",
    minutes: 9,
    alt: "Map of Vaulen and Sørmarka in Stavanger showing the 750 modelled walking routes to Vaulen School today, with line width set by the number of pupils. The routes arrive from several directions, none cross Sørmarkveien, and no street segment carries more than 311.",
  },
  {
    key: "sormarka",
    site: "Interim · Sørmarka",
    minutes: 31,
    alt: "Map of Vaulen and Sørmarka in Stavanger showing the 750 modelled walking routes to the interim school at Sørmarka, with line width set by the number of pupils. The routes converge on Sørmarkveien, where 386 of them would cross at one point.",
  },
];

const CLOSE_UP_ALT = "Close-up map of Sørmarkveien by the interim school at Sørmarka, showing the modelled walking routes to Sørmarka with line width set by the number of pupils. The routes converge on one crossing point, used by 386 of the 750 routes.";

const FlowLines = ({ view }) => (
  <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} aria-hidden="true">
    <g className="vaulen-flows-halo">
      {FLOWS[view].map((d, i) => (d ? <path key={i} d={d} style={{ strokeWidth: `calc(${FLOW_WIDTHS[i]}px * var(--flow-k, 1) + 3px)` }} /> : null))}
    </g>
    <g className={`vaulen-flows ${view === "vaulen" ? "is-today" : ""}`}>
      {FLOWS[view].map((d, i) => (d ? <path key={i} d={d} style={{ strokeWidth: `calc(${FLOW_WIDTHS[i]}px * var(--flow-k, 1))` }} /> : null))}
    </g>
  </svg>
);

const RouteLegend = ({ today = false, className = "" }) => (
  <p className={`vaulen-map-legend ${today ? "is-today" : ""} ${className}`}>
    Pupils per street
    {LEGEND.map(({ label, width }) => <span key={label}><i style={{ height: `calc(${width}px * var(--flow-k, 1))` }} />{label}</span>)}
  </p>
);

const SchoolPin = ({ pos, name, note, side }) => (
  <>
    <span className="vaulen-pin" style={at(...pos)} />
    <span className={`vaulen-map-label vaulen-map-label-${side}`} style={at(...pos)}>{name}<small>{note}</small></span>
  </>
);

// The basemap, routes and labels share one canvas in map pixels. CSS scales and crops the
// canvas so the focus area set on the stage (--fw, --fh, --cx, --cy) always stays in view.
const RouteMap = ({ view, label, className = "", lazy = false, children }) => (
  <div className={`vaulen-stage ${className}`}>
    <div className="vaulen-canvas" role="img" aria-label={label}>
      <img src="/vaulen-basemap.jpg" alt="" width={MAP_W} height={MAP_H} loading={lazy ? "lazy" : "eager"} />
      <FlowLines view={view} />
      {children}
    </div>
    <a className="vaulen-map-credit" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
      © OpenStreetMap contributors
    </a>
  </div>
);

const JourneyShift = () => (
  <figure className="vaulen-shift">
    <div className="vaulen-shift-row">
      <span>Today · Vaulen</span><div className="vaulen-track"><i className="is-today" style={{ width: "29%" }} /></div><strong>9 min</strong>
    </div>
    <div className="vaulen-shift-row">
      <span>Interim · Sørmarka</span><div className="vaulen-track"><i style={{ width: "100%" }} /></div><strong>31 min</strong>
    </div>
    <figcaption>Modelled average walking time per pupil on the footpath network, at 4 km/h</figcaption>
  </figure>
);

const WalkBands = () => (
  <figure className="vaulen-bands">
    <div className="vaulen-bands-legend" aria-hidden="true">
      <span><i className="is-today" />Today · Vaulen</span>
      <span><i />Interim · Sørmarka</span>
    </div>
    <div
      className="vaulen-bands-plot"
      role="img"
      aria-label="Bar chart of modelled pupils by walking time in 5-minute bands. To Vaulen School today, all 750 walk less than 15 minutes. To Sørmarka, every walk is longer than 15 minutes and 442 are longer than 30 minutes."
    >
      {[100, 200, 300].map((v) => (
        <span key={v} className="vaulen-bands-grid" style={{ bottom: `${(v / BAND_MAX) * 100}%` }}><em>{v}</em></span>
      ))}
      {WALK_BANDS.map(({ from, pupils, site }) => (
        <div key={from} className="vaulen-bands-col">
          <i className={site === "today" ? "is-today" : undefined} style={{ height: `${(pupils / BAND_MAX) * 100}%` }} />
        </div>
      ))}
      <span className="vaulen-bands-limit"><b>442 pupils</b> over 30{"\u00a0"}min (2{"\u00a0"}km)</span>
    </div>
    <div className="vaulen-bands-axis" aria-hidden="true">
      <span style={{ left: "0%" }}>0</span>
      <span style={{ left: "33.333%" }}>15</span>
      <span style={{ left: "66.667%" }}>30</span>
      <span style={{ left: "100%" }}>45 min</span>
    </div>
    <figcaption>Modelled pupils by walking time, in 5-minute bands</figcaption>
  </figure>
);

// The hero shows the whole catchment; this figure zooms in on the one crossing.
const CrossingCloseUp = () => (
  <figure className="vaulen-crossing">
    <div className="vaulen-map-bar">
      <p className="vaulen-map-title">Close-up · Routes to Sørmarka</p>
      <RouteLegend />
    </div>
    <RouteMap className="vaulen-closeup" view="sormarka" label={CLOSE_UP_ALT} lazy>
      <span className="vaulen-map-ring" style={at(...CROSSING)} />
      <SchoolPin pos={SCHOOLS.sormarka} name="Sørmarka" note="interim school" side="left" />
      <span className="vaulen-map-label vaulen-map-label-crossing" style={at(...CROSSING)}>
        <strong>386</strong><small>of 750 routes would cross here</small>
      </span>
    </RouteMap>
    <figcaption><b>51%</b> of the 750 modelled pupil routes would use the same crossing on Sørmarkveien.</figcaption>
  </figure>
);

const Hero = () => {
  const [view, setView] = useState("sormarka");
  const current = WALKS.find(({ key }) => key === view);
  return (
    <header className="vaulen-hero">
      <div className="vaulen-hero-copy">
        <Link to="/projects" className="vaulen-back"><ArrowLeft size={15} aria-hidden="true" /> Projects</Link>
        <p className="vaulen-kicker">School mobility · Stavanger, Norway</p>
        <h1>How far Vaulen pupils would walk to <span>Sørmarka</span></h1>
        <p className="vaulen-deck">Vaulen School is due to move to a temporary campus at Sørmarka in 2027 while its 1960 building is demolished and replaced. We routed the walk from home to each site for 750 modelled pupils.</p>
      </div>

      <div className="vaulen-walk">
        <p className="vaulen-walk-title" id="vaulen-walk-title">Average walk per pupil <span>modelled</span></p>
        <div className="vaulen-walk-pair" role="group" aria-labelledby="vaulen-walk-title">
          {WALKS.map(({ key, site, minutes }, i) => (
            <React.Fragment key={key}>
              {i > 0 && <ArrowRight className="vaulen-walk-arrow" size={20} aria-hidden="true" />}
              <button
                type="button"
                className={key === "vaulen" ? "is-today" : undefined}
                aria-pressed={view === key}
                onClick={() => setView(key)}
              >
                <span className="vaulen-walk-site"><i aria-hidden="true" />{site}</span>
                <strong>{minutes}<small>{"\u00a0"}min</small></strong>
                <span className="vaulen-walk-state" aria-hidden="true">{view === key ? "Routes on map" : "Show routes"}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      <RouteMap className="vaulen-hero-map" view={view} label={current.alt}>
        <SchoolPin pos={SCHOOLS.sormarka} name="Sørmarka" note="interim school" side="left" />
        <SchoolPin pos={SCHOOLS.vaulen} name="Vaulen School" note="today" side="right" />
      </RouteMap>
      <RouteLegend today={view === "vaulen"} className="vaulen-hero-legend" />

      <div className="vaulen-meta">
        <span><Route size={15} aria-hidden="true" />Routed on OpenStreetMap footpaths</span>
        <span><CalendarClock size={15} aria-hidden="true" />Move planned for 2027</span>
      </div>
    </header>
  );
};

const VaulenSchoolPage = () => (
  <div className="vaulen-page">
    <Navbar />
    <main className="pt-16">
      <Hero />

      <section className="vaulen-highlights" aria-label="Modelled figures">
        <p className="vaulen-highlights-tag">Modelled for the planned move</p>
        {highlights.map(({ label, value }) => <div key={label}><small>{label}</small><strong>{value}</strong></div>)}
      </section>

      <section className="vaulen-intro vaulen-wrap">
        <div><p className="vaulen-label">The move</p><h2>Pupils could be at Sørmarka for several years.</h2></div>
        <div>
          <ol className="vaulen-timeline">
            {timeline.map(({ when, what }) => <li key={when}><strong>{when}</strong><span>{what}</span></li>)}
          </ol>
          <p>We placed 750 modelled pupils in the 1,329 homes closest to Vaulen School and routed each walk to both sites. With the school's own address list, the same routing can be run for the actual pupils.</p>
        </div>
      </section>

      <section className="vaulen-distance">
        <div className="vaulen-wrap vaulen-distance-grid">
          <div>
            <p className="vaulen-label">Walking time</p>
            <h2>The average walk would more than triple.</h2>
            <p>Today every modelled pupil is within a 15-minute walk of Vaulen School. None would be within 15 minutes of Sørmarka, and 442 of the 750 would walk for more than 30 minutes (2 km at 4 km/h).</p>
          </div>
          <div className="vaulen-distance-figs">
            <JourneyShift />
            <WalkBands />
          </div>
        </div>
      </section>

      <section className="vaulen-crossing-section vaulen-wrap">
        <div>
          <p className="vaulen-label">Crossings</p>
          <h2>Half the routes would use one crossing on Sørmarkveien.</h2>
          <p>Every modelled route to Sørmarka would cross Sørmarkveien, 386 of them at the same point. Today no modelled route crosses Sørmarkveien. The routes reach Vaulen School from several directions, and no street segment carries more than 311.</p>
          <p>The model counts walking routes only. Whether a crossing is safe also depends on traffic, speed and design, which it does not include.</p>
        </div>
        <CrossingCloseUp />
      </section>

      <section className="vaulen-actions">
        <div className="vaulen-wrap">
          <p className="vaulen-label">Planning</p>
          <h2>How the results can be used</h2>
          <div className="vaulen-action-grid">
            <article>
              <ShieldCheck aria-hidden="true" />
              <h3>Check the busiest crossing</h3>
              <p>Half the modelled routes would cross Sørmarkveien at one point. Crossing design, lighting and winter maintenance there would affect the most pupils.</p>
              <strong>51% of routes</strong>
            </article>
            <article>
              <Bus aria-hidden="true" />
              <h3>Find who qualifies for school transport</h3>
              <p>Under the Education Act, first graders who live more than 2 km from school have a right to free transport; from 2nd grade the limit is 4 km. A particularly dangerous or difficult route qualifies at any distance. No modelled home is more than 4 km from Sørmarka, so on distance alone only first graders would qualify.</p>
              <strong>442 pupils over 2 km</strong>
            </article>
            <article>
              <Radio aria-hidden="true" />
              <h3>Measure the change</h3>
              <p>Presence can be counted in zones around both schools every 10 minutes, before and after the move, to check whether activity shifts the way the model expects. The counts are of mobile devices in each zone, so parents, staff and arena visitors are included too.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="vaulen-method" aria-labelledby="vaulen-method-title">
        <h2 id="vaulen-method-title">About the figures</h2>
        <p>Every figure on this page is modelled for the planned move. Pupil homes are estimated from Statistics Norway population counts for small areas, spread across OpenStreetMap residential buildings by housing type, and the 750 modelled pupils living closest to Vaulen School form the catchment. No pupil records were used. Walks are routed on the OpenStreetMap footpath network at 4 km/h, so 2 km is a 30-minute walk. The route map leaves out streets used by fewer than 5 modelled pupils. Map © OpenStreetMap contributors.</p>
      </section>

      <CaseStudyCta
        question="Is a school in your area moving or changing its catchment?"
        body="We can route every pupil's walk to each site, count the road crossings and find who lives beyond the free-transport limits, from open data or from the school's own address list."
      />
    </main>
  </div>
);

export default VaulenSchoolPage;
